/**
 * Multi-select input — button-grid design (Step 6).
 *
 * Each option is a button; clicking toggles it independently. Selected
 * buttons are outlined. Stored value is `option_key[]`.
 */
import { cn } from '@/lib/utils/cn'
import type { SelectOption } from '@/features/form-builder/api/form-builder-api'
import type { InputProps } from '../input-props'

export function MultiSelectInput({
  field, value, onChange, onBlur, disabled, filteredOptions, invalid,
}: InputProps) {
  const allOptions = (field.type_config?.options as SelectOption[] | undefined) ?? []
  const options = filteredOptions
    ? allOptions.filter((o) => filteredOptions.includes(o.option_key))
    : allOptions
  const selected = (value as string[]) ?? []

  if (options.length === 0) {
    return (
      <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2">
        گزینه‌ای تعریف نشده است
      </div>
    )
  }

  const toggle = (key: string) => {
    if (disabled) return
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key]
    onChange(next)
  }

  return (
    <div className="flex flex-col gap-1.5" onBlur={onBlur}>
      {options.map((opt) => {
        const isOn = selected.includes(opt.option_key)
        return (
          <button
            key={opt.option_key}
            type="button"
            onClick={() => toggle(opt.option_key)}
            disabled={disabled}
            aria-pressed={isOn}
            className={cn(
              'w-full rounded-md border px-3 py-2 text-sm text-start transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-background',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              isOn
                ? 'border-primary bg-primary/15 text-primary ring-1 ring-primary'
                : 'border-border bg-background text-foreground hover:border-foreground/40',
              invalid && selected.length === 0 && 'border-destructive/50',
            )}
          >
            {opt.label?.fa || opt.option_key}
          </button>
        )
      })}
    </div>
  )
}
